import React, {
	createContext,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
	useCallback,
} from "react";
import { chatSdk } from "./cometchat/chat_client";
import { useCometChatAuth } from "./cometchat_auth_context";

const UnreadContext = createContext(null);

const UNREAD_SYNC_CHANNEL = "cc_unread_sync_v1";
const UNREAD_SYNC_STORAGE_KEY = "cc_unread_sync_ping_v1";

// chat key: user:UID | group:GUID
const makeKey = (type, id) => `${type}:${id}`;

export function CometChatUnreadProvider({ children }) {
	const { isUiKitInitialized, loggedInUser } = useCometChatAuth();

	const [counts, setCounts] = useState({}); // { 'user:123': 2, 'group:abc': 5 }
	const activeRef = useRef(null); // { type: 'user'|'group', id: string } | null
	const listenerId = useRef("UNREAD_" + Math.random().toString(36).slice(2));
	const tabIdRef = useRef("TAB_" + Math.random().toString(36).slice(2));
	const broadcastChannelRef = useRef(null);

	// keep my UID in a ref to avoid stale closures across renders
	const myUidRef = useRef(null);
	myUidRef.current = loggedInUser?.uid ?? null;

	const syncFromServer = useCallback(async () => {
		if (!isUiKitInitialized || !loggedInUser) return;

		try {
			const res = await chatSdk().getUnreadMessageCount();

			const next = {};
			Object.entries(res.users ?? {}).forEach(([uid, n]) => {
				next[makeKey("user", uid)] = n;
			});
			Object.entries(res.groups ?? {}).forEach(([gid, n]) => {
				next[makeKey("group", gid)] = n;
			});

			// The currently open chat is always considered read. Force its count
			// to 0 so that a stale server response (markAsRead not yet processed)
			// cannot overwrite the local 0 set by reset()/markChatReadUpTo().
			const active = activeRef.current;
			if (active) {
				const activeKey = makeKey(active.type, active.id);
				next[activeKey] = 0;
			}

			setCounts(next);
		} catch {
			// no-op
		}
	}, [isUiKitInitialized, loggedInUser]);

	const notifyUnreadSync = useCallback(() => {
		const payload = {
			type: "UNREAD_SYNC",
			source: tabIdRef.current,
			at: Date.now(),
		};

		try {
			broadcastChannelRef.current?.postMessage(payload);
		} catch {
			// no-op
		}

		try {
			localStorage.setItem(UNREAD_SYNC_STORAGE_KEY, JSON.stringify(payload));
			localStorage.removeItem(UNREAD_SYNC_STORAGE_KEY);
		} catch {
			// no-op
		}
	}, []);

	// --- public API (stable references) ---
	const setActiveChat = useCallback(chat => {
		// chat = { type: 'user'|'group', id: string } | null
		activeRef.current = chat;
	}, []);

	const getCount = useCallback((type, id) => counts[makeKey(type, id)] ?? 0, [counts]);

	const total = useMemo(() => Object.values(counts).reduce((a, b) => a + b, 0), [counts]);

	const setCount = useCallback((type, id, n) => {
		const key = makeKey(type, id);
		setCounts(s => (s[key] === n ? s : { ...s, [key]: n }));
	}, []);

	const inc = useCallback((type, id) => {
		const key = makeKey(type, id);
		setCounts(s => ({ ...s, [key]: (s[key] ?? 0) + 1 }));
	}, []);

	const reset = useCallback((type, id) => {
		const key = makeKey(type, id);
		setCounts(s => {
			if (!s[key]) return s; // already 0 — avoid extra render
			return { ...s, [key]: 0 };
		});
	}, []);

	// safe markAsRead wrapper (supports both SDK signatures)
	const markMessageAsRead = useCallback(m => {
		try {
			if (typeof chatSdk().markAsRead === "function") {
				chatSdk().markAsRead(m);
			}
		} catch {
			//
		}
	}, []);

	// 1) initial sync after login
	useEffect(() => {
		let cancelled = false;

		async function bootstrap() {
			if (!isUiKitInitialized) return;
			// Logged out: drop the counts, or the next account to log in on this tab
			// sees the previous one's badge until its own sync lands.
			if (!loggedInUser) {
				setCounts({});
				return;
			}

			try {
				// { users: {uid: n}, groups: {guid: n} }
				const res = await chatSdk().getUnreadMessageCount();
				if (cancelled) return;

				const next = {};
				Object.entries(res.users ?? {}).forEach(([uid, n]) => {
					next[makeKey("user", uid)] = n;
				});
				Object.entries(res.groups ?? {}).forEach(([gid, n]) => {
					next[makeKey("group", gid)] = n;
				});
				setCounts(next);
			} catch {
				// it's fine; we'll rely on live listeners only
			}
		}

		bootstrap();
		return () => {
			cancelled = true;
		};
	}, [isUiKitInitialized, loggedInUser]);

	// 1.1) cross-tab sync listeners + focus sync
	useEffect(() => {
		if (!isUiKitInitialized || !loggedInUser) return;

		if (typeof BroadcastChannel !== "undefined") {
			try {
				broadcastChannelRef.current = new BroadcastChannel(UNREAD_SYNC_CHANNEL);
				broadcastChannelRef.current.onmessage = event => {
					const payload = event?.data;
					if (!payload || payload.source === tabIdRef.current) return;
					if (payload.type !== "UNREAD_SYNC") return;
					syncFromServer();
				};
			} catch {
				broadcastChannelRef.current = null;
			}
		}

		const onStorage = event => {
			if (event.key !== UNREAD_SYNC_STORAGE_KEY || !event.newValue) return;

			try {
				const payload = JSON.parse(event.newValue);
				if (!payload || payload.source === tabIdRef.current) return;
				if (payload.type !== "UNREAD_SYNC") return;
				syncFromServer();
			} catch {
				// no-op
			}
		};

		const onFocus = () => {
			syncFromServer();
		};

		const onVisibilityChange = () => {
			if (document.visibilityState === "visible") {
				syncFromServer();
			}
		};

		window.addEventListener("storage", onStorage);
		window.addEventListener("focus", onFocus);
		document.addEventListener("visibilitychange", onVisibilityChange);

		return () => {
			window.removeEventListener("storage", onStorage);
			window.removeEventListener("focus", onFocus);
			document.removeEventListener("visibilitychange", onVisibilityChange);
			try {
				broadcastChannelRef.current?.close?.();
			} catch {
				// no-op
			}
			broadcastChannelRef.current = null;
		};
	}, [isUiKitInitialized, loggedInUser, syncFromServer]);

	// 2) message listeners (only count INCOMING messages)
	useEffect(() => {
		if (!isUiKitInitialized || !loggedInUser) return;

		const LID = listenerId.current;

		const handleIncoming = m => {
			const myUid = myUidRef.current;
			if (!m || !myUid) return;

			// optional: ignore non-message categories (calls, actions, etc.)
			// const category = m.getCategory?.() ?? m.category;
			// if (category && category !== "message") return;

			const receiverType = m.getReceiverType?.() ?? m.receiverType;
			const senderUid = m.getSender?.()?.getUid?.() ?? m.sender?.uid;
			const receiverId = m.getReceiverId?.() ?? m.receiverId;

			// INCOMING-ONLY filter:
			// - 1:1 => incoming if the message is addressed to me (receiverId === myUid)
			// - group => incoming if I am NOT the sender (senderUid !== myUid)
			if (receiverType === "user") {
				if (receiverId !== myUid) return; // it's my outgoing message => ignore
			} else if (receiverType === "group") {
				if (senderUid === myUid) return; // my own message in group => ignore
			} else {
				// unknown type => ignore
				return;
			}

			// Build the counter key:
			// - group: count per group GUID
			// - 1:1: count per SENDER (the other participant)
			let key;
			if (receiverType === "group") {
				key = { type: "group", id: receiverId };
			} else {
				key = { type: "user", id: senderUid };
			}
			if (!key?.id) return;

			// If this chat is currently active, mark as read and reset local counter;
			// otherwise increment.
			const active = activeRef.current;
			const isActive = !!active && active.type === key.type && active.id === key.id;

			if (isActive) {
				markMessageAsRead(m); // it's incoming and visible => mark read
				reset(key.type, key.id);
				notifyUnreadSync();
			} else {
				inc(key.type, key.id);
			}
		};

		chatSdk().addMessageListener(
			LID,
			new (chatSdk().MessageListener)({
				onTextMessageReceived: handleIncoming,
				onMediaMessageReceived: handleIncoming,
				onCustomMessageReceived: handleIncoming,
				onMessagesRead: () => {
					syncFromServer();
				},
			}),
		);

		return () => {
			chatSdk().removeMessageListener?.(LID);
		};
	}, [
		isUiKitInitialized,
		loggedInUser,
		markMessageAsRead,
		reset,
		inc,
		syncFromServer,
		notifyUnreadSync,
	]);

	// 3) helper to mark chat as read up to a known message (e.g., after loading history)
	const markChatReadUpTo = useCallback(
		(type, id, lastMessage) => {
			if (lastMessage) {
				try {
					markMessageAsRead(lastMessage);
				} catch {
					// no-op
				}
			}
			reset(type, id);
			notifyUnreadSync();
		},
		[markMessageAsRead, reset, notifyUnreadSync],
	);

	const value = useMemo(
		() => ({
			// read
			total,
			getCount,
			// control active chat (so incoming messages in an open chat are auto-read)
			setActiveChat,
			// manual reset/mark helper
			markChatReadUpTo,
			// optionally expose direct mutators
			setCount,
			inc,
			reset,
		}),
		[total, getCount, setActiveChat, markChatReadUpTo, setCount, inc, reset],
	);

	return <UnreadContext.Provider value={value}>{children}</UnreadContext.Provider>;
}

export function useCometChatUnread() {
	const ctx = useContext(UnreadContext);
	if (!ctx) throw new Error("useCometChatUnread must be inside CometChatUnreadProvider");
	return ctx;
}
